/**
 * Fullscreen API 유틸리티
 * 브라우저의 Fullscreen API를 사용하여 전체 화면 모드를 제어합니다.
 */

/**
 * 전체 화면 모드 지원 여부 확인
 */
export function isFullscreenSupported(): boolean {
  return !!(
    document.fullscreenEnabled ||
    (document as any).webkitFullscreenEnabled ||
    (document as any).mozFullScreenEnabled ||
    (document as any).msFullscreenEnabled
  );
}

/**
 * 현재 전체 화면 모드인지 확인
 */
export function isFullscreen(): boolean {
  return !!(
    document.fullscreenElement ||
    (document as any).webkitFullscreenElement ||
    (document as any).mozFullScreenElement ||
    (document as any).msFullscreenElement
  );
}

/**
 * 전체 화면 모드로 전환
 */
export async function enterFullscreen(): Promise<boolean> {
  if (!isFullscreenSupported()) {
    console.warn('⚠️ [Fullscreen] 전체 화면 모드를 지원하지 않습니다.');
    return false;
  }

  if (isFullscreen()) {
    console.log('ℹ️ [Fullscreen] 이미 전체 화면 모드입니다.');
    return true;
  }

  try {
    const element = document.documentElement;

    if (element.requestFullscreen) {
      await element.requestFullscreen();
    } else if ((element as any).webkitRequestFullscreen) {
      await (element as any).webkitRequestFullscreen();
    } else if ((element as any).mozRequestFullScreen) {
      await (element as any).mozRequestFullScreen();
    } else if ((element as any).msRequestFullscreen) {
      await (element as any).msRequestFullscreen();
    } else {
      console.warn('⚠️ [Fullscreen] 전체 화면 API를 사용할 수 없습니다.');
      return false;
    }

    console.log('✅ [Fullscreen] 전체 화면 모드로 전환되었습니다.');
    return true;
  } catch (error) {
    console.error('❌ [Fullscreen] 전체 화면 전환 실패:', error);
    return false;
  }
}

/**
 * 전체 화면 모드 종료
 */
export async function exitFullscreen(): Promise<boolean> {
  if (!isFullscreen()) {
    console.log('ℹ️ [Fullscreen] 이미 일반 모드입니다.');
    return true;
  }

  try {
    if (document.exitFullscreen) {
      await document.exitFullscreen();
    } else if ((document as any).webkitExitFullscreen) {
      await (document as any).webkitExitFullscreen();
    } else if ((document as any).mozCancelFullScreen) {
      await (document as any).mozCancelFullScreen();
    } else if ((document as any).msExitFullscreen) {
      await (document as any).msExitFullscreen();
    } else {
      console.warn('⚠️ [Fullscreen] 전체 화면 종료 API를 사용할 수 없습니다.');
      return false;
    }

    console.log('✅ [Fullscreen] 일반 모드로 전환되었습니다.');
    return true;
  } catch (error) {
    console.error('❌ [Fullscreen] 전체 화면 종료 실패:', error);
    return false;
  }
}

/**
 * 전체 화면 모드 토글
 */
export async function toggleFullscreen(): Promise<boolean> {
  if (isFullscreen()) {
    return await exitFullscreen();
  } else {
    return await enterFullscreen();
  }
}

/**
 * 전체 화면 상태 변경 이벤트 리스너 등록
 */
export function onFullscreenChange(callback: (isFullscreen: boolean) => void): () => void {
  const handleChange = () => {
    callback(isFullscreen());
  };

  // 다양한 브라우저의 이벤트 리스너 등록
  document.addEventListener('fullscreenchange', handleChange);
  document.addEventListener('webkitfullscreenchange', handleChange);
  document.addEventListener('mozfullscreenchange', handleChange);
  document.addEventListener('MSFullscreenChange', handleChange);

  // 클린업 함수 반환
  return () => {
    document.removeEventListener('fullscreenchange', handleChange);
    document.removeEventListener('webkitfullscreenchange', handleChange);
    document.removeEventListener('mozfullscreenchange', handleChange);
    document.removeEventListener('MSFullscreenChange', handleChange);
  };
}

